import { useFrame, useThree } from '@react-three/fiber'
import type { FunctionComponent } from 'react'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { Group } from 'three'
import { AnimationMixer, Mesh } from 'three'

import { DISABLE_FRAME, ENABLE_FRAME } from '../../components/canvas/constants'
import { Card } from '../../components/card'
import { Phone } from '../../components/phone'
import type { GroupWithControls } from '../../components/phone'
import { Transitions } from '../../components/phone/constants'
import { useIsMobile } from '../../lib/hooks/useMedia'
import { useMouse } from '../../lib/hooks/useMouse'
import { useScroll } from '../../lib/hooks/useScroll'
import { range } from '../../lib/utils/range'
import { cardAnim, phoneAnim } from './constants'

export const HomepageHeroComposition: FunctionComponent<
    HomepageHeroCompositionProps
> = ({ screens, inView, onLoad }) => {
    const {
        gl: { domElement },
        invalidate,
    } = useThree()
    const isMobile = useIsMobile()
    const prevScreenIndexRef = useRef<number>(0)
    const { controller: scroll } = useScroll(invalidate)
    const { controller: mouse } = useMouse(invalidate)

    const phoneRef = useRef<GroupWithControls | null>(null)
    const phoneMixerRoot = useRef<Mesh | null>(null)
    const phoneMixer = useRef<AnimationMixer | null>(null)
    const cardRef = useRef<Group | null>(null)
    const cardMixerRoot = useRef<Mesh | null>(null)
    const cardMixer = useRef<AnimationMixer | null>(null)
    const [cardLoaded, setCardLoaded] = useState(false)
    const [phoneLoaded, setPhoneLoaded] = useState(false)

    const createAnimationMixers = useCallback(() => {
        phoneMixerRoot.current = new Mesh()
        phoneMixer.current = new AnimationMixer(phoneMixerRoot.current)
        const phoneAction = phoneMixer.current.clipAction(
            phoneAnim[isMobile ? 0 : 1]
        )
        phoneAction.play()

        cardMixerRoot.current = new Mesh()
        cardMixer.current = new AnimationMixer(cardMixerRoot.current)
        const cardAction = cardMixer.current.clipAction(
            cardAnim[isMobile ? 0 : 1]
        )
        cardAction.play()

        // fix for inconsistent interpolation bug in THREE.InterpolateSmooth
        phoneMixer.current.setTime(1)
        cardMixer.current.setTime(1)
    }, [isMobile])

    useEffect(() => {
        phoneLoaded && cardLoaded && onLoad && onLoad()
    }, [cardLoaded, onLoad, phoneLoaded])

    useFrame(
        () => {
            if (!phoneRef.current) return
            const scrollProgress = scroll.get().progress
            if (phoneRef.current && cardRef.current) {
                // update phone screen
                const rScreen = range(scrollProgress, 0, 0.125)
                const activeScreenIndex = Math.floor(rScreen)

                if (activeScreenIndex < prevScreenIndexRef.current) {
                    phoneRef.current.prevScreen()
                } else if (activeScreenIndex > prevScreenIndexRef.current) {
                    phoneRef.current.nextScreen()
                }
                prevScreenIndexRef.current = activeScreenIndex

                // create animation mixers if they are not already
                if (!phoneMixer.current || !cardMixer.current) {
                    createAnimationMixers()
                }

                if (
                    phoneMixer.current &&
                    cardMixer.current &&
                    phoneMixerRoot.current &&
                    cardMixerRoot.current
                ) {
                    // udpate animaton mixer time
                    phoneMixer.current?.setTime(scrollProgress)
                    cardMixer.current?.setTime(scrollProgress)

                    phoneRef.current.obj.rotation.copy(
                        phoneMixerRoot.current.rotation
                    )
                    phoneRef.current.obj.position.copy(
                        phoneMixerRoot.current.position
                    )
                    phoneRef.current.obj.scale.copy(
                        phoneMixerRoot.current.scale
                    )

                    cardRef.current.rotation.copy(
                        cardMixerRoot.current.rotation
                    )
                    cardRef.current.position.copy(
                        cardMixerRoot.current.position
                    )
                    cardRef.current.scale.copy(cardMixerRoot.current.scale)

                    // apply rotations from mouse position
                    phoneRef.current.obj.rotation.y += mouse.get().x / 10
                    phoneRef.current.obj.rotation.z += mouse.get().y / 20
                    cardRef.current.rotation.y += mouse.get().x / 10
                    cardRef.current.rotation.z += mouse.get().y / 20
                }
            }
        },
        inView ? ENABLE_FRAME : DISABLE_FRAME
    )

    useEffect(() => {
        // update animation mixers when breakpoint changes
        if (phoneMixer.current && cardMixer.current) {
            createAnimationMixers()
        }
    }, [createAnimationMixers, isMobile])

    useEffect(() => {
        if (
            cardLoaded &&
            phoneLoaded &&
            domElement?.parentElement?.parentElement
        ) {
            domElement.parentElement.parentElement.style.opacity = '1'
        }
    }, [domElement, cardLoaded, phoneLoaded])

    return (
        <Suspense fallback={null}>
            <Phone
                ref={phoneRef}
                screens={screens}
                transition={Transitions.FADE}
                onLoad={() => setPhoneLoaded(true)}
            />
            <Card ref={cardRef} onLoad={() => setCardLoaded(true)} />
        </Suspense>
    )
}

export interface HomepageHeroCompositionData {
    screens: [string, string]
}

interface HomepageHeroCompositionProps extends HomepageHeroCompositionData {
    inView: boolean
    onLoad?: () => void
}
